import { useEffect, useState } from 'react';
import { IUserManagementServiceInjectedProps } from '$State/UserManagementFreezerService';
import { IConfigServiceInjectedProps } from '$State/ConfigFreezerService';
import { NavigateFunction, Location } from 'react-router-dom';
import { StandardPublicRoutePaths } from "../../router/route-sets/standard-route-config";

export function useLogin(
    userManagement: IUserManagementServiceInjectedProps['userManagement'],
    config: IConfigServiceInjectedProps['config'],
    navigate?: NavigateFunction,
    location?: Location,
) {
    const [isReady, setIsReady] = useState(false);
    const [loginAttempted, setLoginAttempted] = useState(false);
    const isLoggedIn = userManagement.isLoggedIn();

    // Once logged in and ready, we're done
    const isAuthenticated = isLoggedIn && isReady;

    useEffect(() => {
        // If already logged in, mark as ready immediately
        if (isLoggedIn) {
            setIsReady(true);
            setLoginAttempted(true);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        let cancelled = false;

        const attempt = async () => {
            // Already logged in, nothing to do
            if (userManagement.isLoggedIn()) {
                if (!cancelled) {
                    setIsReady(true);
                    setLoginAttempted(true);
                }
                return;
            }

            if (config.isRunningStandAlone()) {
                const loginSuccess = await userManagement.tryLogin();

                if (cancelled) return;

                setLoginAttempted(true);

                if (!loginSuccess) {
                    if (navigate && location) {
                        navigate(StandardPublicRoutePaths.Login, { state: { from: location }, replace: true });
                    }
                    return;
                }

                if (userManagement.isLoggedIn()) {
                    setIsReady(true);
                }
            } else {
                // Integrated modes: wait for external credential provisioning
                setLoginAttempted(true);
            }
        };

        attempt();

        return () => {
            cancelled = true;
        };
    }, []);

    // For integrated mode, watch for credential changes
    useEffect(() => {
        if (loginAttempted && !config.isRunningStandAlone() && isLoggedIn && !isReady) {
            setIsReady(true);
        }
    }, [loginAttempted, isLoggedIn, isReady, config]);

    return isAuthenticated;
}
